import { supabase } from './supabase';
import { compressImage } from './image-compress';
import type {
  Post,
  Comment,
  Drawer,
  Profile,
  PollOption,
  PostEvent,
  PostAuction,
  AuctionBid,
  MemberPaymentMethod,
  AuctionCharge,
  EventLocationKind,
  AuctionState,
  Media,
  PostButton,
  Submission,
  CulturalEvent,
  CulturalEventCategory,
} from './types';

/**
 * All Supabase reads/writes the app uses. Keeping them in one file lets the
 * UI stay unaware of Supabase-specific shapes and makes the join plan easy
 * to reason about.
 */

// ---------- Drawers -------------------------------------------------------

export async function fetchDrawers(signal?: AbortSignal): Promise<Drawer[]> {
  let q = supabase
    .from('drawers')
    .select('id, slug, name, description, cover_image_url, sort_order, is_published')
    .eq('is_published', true)
    .order('sort_order', { ascending: true });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Drawer[];
}

function slugifyName(s: string): string {
  return s
    .toLowerCase()
    .replace(/['â€™]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * Admin-only: create a new closet (drawer). Slug is derived from the name; if
 * that slug already exists we append a short random suffix to keep it unique.
 * sort_order lands after the current max so the new closet appears last;
 * is_published defaults true so it shows in the wizard + feed immediately.
 */
export async function createDrawer(name: string): Promise<Drawer> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('A closet name is required.');

  const base = slugifyName(trimmed) || 'closet';

  // Existing slugs + the current max sort_order in one read.
  const { data: existing, error: exErr } = await supabase
    .from('drawers')
    .select('slug, sort_order');
  if (exErr) throw exErr;
  const taken = new Set((existing ?? []).map((r: any) => r.slug as string));
  const maxSort = (existing ?? []).reduce(
    (m: number, r: any) => Math.max(m, r.sort_order ?? 0),
    0,
  );

  let slug = base;
  while (taken.has(slug)) {
    slug = `${base}-${Math.random().toString(36).slice(2, 6)}`;
  }

  const { data, error } = await supabase
    .from('drawers')
    .insert({
      slug,
      name: trimmed,
      sort_order: maxSort + 1,
      is_published: true,
    })
    .select('id, slug, name, description, cover_image_url, sort_order, is_published')
    .single();
  if (error) throw error;
  return data as Drawer;
}

// ---------- Closets -------------------------------------------------------
// The feed renders one "closet" per published drawer, each fronting that
// drawer's single most-recent live post. Older posts of the same type fall
// into the Archive automatically â€” "most recent" is just the newest
// published_at; everything else is history. A drawer with no live post yet
// renders as a closed, empty cabinet (post === null).

export interface Closet {
  drawer: Drawer;
  /** Newest live post in this drawer, or null if the drawer has none yet. */
  post: Post | null;
}

/**
 * Build the closet wall: every published drawer paired with its newest live
 * post. Correctness over a naive "fetch recent N and group" approach â€” a
 * low-cadence drawer (e.g. Monthly Q&A) could be pushed past any flat limit
 * by high-cadence ones, so we resolve the front post id per drawer directly,
 * then hydrate the (small, deduped) set in one round-trip + parallel feature
 * attach. Feature blocks let each closet stamp Video/Poll/Event/Auction.
 */
export async function fetchClosets(signal?: AbortSignal, sinceIso?: string | null): Promise<Closet[]> {
  const drawers = await fetchDrawers(signal);
  if (drawers.length === 0) return [];
  const nowIso = new Date().toISOString();

  // One tiny query per drawer: newest live post id in that drawer. Runs in
  // parallel â€” bounded by the (handful of) published drawers. We query from
  // the posts side (not post_drawers) so the ORDER BY applies to the parent
  // rows; ordering by an *embedded* column would only sort the nested array,
  // not pick the newest post. The inner join on post_drawers scopes posts to
  // this drawer.
  const fronts = await Promise.all(
    drawers.map(async (d) => {
      let q = supabase
        .from('posts')
        .select('id, post_drawers!inner ( drawer_id )')
        .eq('post_drawers.drawer_id', d.id)
        .not('published_at', 'is', null)
        .lte('published_at', nowIso)
        .order('published_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1);
      // Cycle floor: only count posts released in the current cycle as the
      // closet's front. Older posts fall to the Archive; the closet then reads
      // as "Coming soon" until this cycle's drop lands.
      if (sinceIso) q = q.gte('published_at', sinceIso);
      if (signal) q = q.abortSignal(signal);
      const { data, error } = await q;
      if (error) throw error;
      const row = (data ?? [])[0] as any;
      return { drawerId: d.id, postId: (row?.id as string | undefined) ?? null };
    }),
  );

  const frontIds = [...new Set(fronts.map((f) => f.postId).filter(Boolean) as string[])];
  if (frontIds.length === 0) {
    return drawers.map((drawer) => ({ drawer, post: null }));
  }

  // Hydrate the front posts with the full select (so each carries all its
  // drawers, media, author), then attach feature blocks in parallel.
  let pq = supabase.from('posts_with_counts').select(POST_SELECT).in('id', frontIds);
  if (signal) pq = pq.abortSignal(signal);
  const { data: postRows, error: pErr } = await pq;
  if (pErr) throw pErr;
  const posts = (postRows ?? []).map(shapePost);
  const byId = new Map(posts.map((p) => [p.id, p]));

  const viewerId = (await supabase.auth.getUser()).data.user?.id ?? null;
  const [pollsByPost, eventsByPost, auctionsByPost] = await Promise.all([
    fetchPollOptionsForPosts(frontIds, null, signal),
    fetchEventsForPosts(frontIds, viewerId, signal),
    fetchAuctionsForPosts(frontIds, viewerId, signal),
  ]);
  for (const p of posts) {
    const opts = pollsByPost.get(p.id);
    if (opts && opts.length > 0) p.poll_options = opts;
    const ev = eventsByPost.get(p.id);
    if (ev) p.event = ev;
    const auc = auctionsByPost.get(p.id);
    if (auc) p.auction = auc;
  }

  const postByDrawer = new Map<string, string>();
  for (const f of fronts) if (f.postId) postByDrawer.set(f.drawerId, f.postId);

  return drawers.map((drawer) => ({
    drawer,
    post: byId.get(postByDrawer.get(drawer.id) ?? '') ?? null,
  }));
}

// ---------- Posts ---------------------------------------------------------
// Reads go through the `posts_with_counts` view so like_count + comment_count
// come back from the DB instead of being computed client-side.

const POST_SELECT = `
  id, slug, title, dek, body_md, post_type, format, buttons, resource_url,
  thumbnail_url, hide_title, author_id, primary_media_id,
  external_url, image_urls, published_at, created_at,
  like_count, comment_count,
  media:media!posts_primary_media_fk (
    id, kind, provider, provider_id, aspect_ratio, duration_sec,
    thumbnail_url, width, height, status
  ),
  post_drawers (
    drawers ( id, slug, name, sort_order )
  ),
  author:profiles!posts_author_id_fkey (
    id, display_name, avatar_url, handle
  )
`;

function shapePost(row: any): Post {
  const drawers: Drawer[] = Array.isArray(row.post_drawers)
    ? row.post_drawers.map((x: any) => x.drawers).filter(Boolean)
    : [];
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    dek: row.dek,
    body_md: row.body_md,
    post_type: row.post_type,
    format: row.format ?? 'standard',
    author_id: row.author_id,
    primary_media_id: row.primary_media_id,
    external_url: row.external_url,
    image_urls: Array.isArray(row.image_urls) ? row.image_urls : [],
    buttons: Array.isArray(row.buttons) ? (row.buttons as PostButton[]) : [],
    resource_url: row.resource_url ?? null,
    thumbnail_url: row.thumbnail_url ?? null,
    hide_title: row.hide_title ?? false,
    published_at: row.published_at,
    created_at: row.created_at,
    media: row.media ?? null,
    drawers,
    author: row.author ?? undefined,
    like_count: row.like_count ?? 0,
    comment_count: row.comment_count ?? 0,
  };
}

// The media columns we hydrate for the multi-media viewer, shared by the
// post_media join below and the shapeMedia mapper.
const MEDIA_SELECT =
  'id, kind, provider, provider_id, aspect_ratio, duration_sec, thumbnail_url, width, height, status';

function shapeMedia(row: any): Media {
  return {
    id: row.id,
    kind: row.kind,
    provider: row.provider,
    provider_id: row.provider_id,
    aspect_ratio: row.aspect_ratio ?? null,
    duration_sec: row.duration_sec ?? null,
    thumbnail_url: row.thumbnail_url ?? null,
    width: row.width ?? null,
    height: row.height ?? null,
    status: row.status,
  };
}

/**
 * Build the ordered media_items for a post.
 *
 * Source of truth is the post_media table (joined to media, ordered by
 * position). For older/unbackfilled posts with no post_media rows, fall back
 * to the existing data: the single primary `media` for video posts, or each
 * entry in image_urls synthesised into an image-kind Media so the viewer
 * still renders.
 */
async function fetchPostMediaItems(post: Post, signal?: AbortSignal): Promise<Media[]> {
  let q = supabase
    .from('post_media')
    .select(`position, media:media!post_media_media_id_fkey ( ${MEDIA_SELECT} )`)
    .eq('post_id', post.id)
    .order('position', { ascending: true });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;

  const items = (data ?? [])
    .map((r: any) => (Array.isArray(r.media) ? r.media[0] : r.media))
    .filter(Boolean)
    .map(shapeMedia);
  if (items.length > 0) return items;

  // Fallback for posts that predate post_media (or weren't backfilled).
  if (post.media && (post.post_type === 'video' || post.media.kind === 'video')) {
    return [post.media];
  }
  const urls = post.image_urls ?? [];
  return urls.map((url) => ({
    id: `synthetic:${url}`,
    kind: 'image' as const,
    provider: 'supabase_storage' as const,
    provider_id: url,
    aspect_ratio: null,
    duration_sec: null,
    thumbnail_url: url,
    width: null,
    height: null,
    status: 'ready' as const,
  }));
}

export interface FeedCursor {
  published_at: string;
  id: string;
}

export interface FeedPage {
  items: Post[];
  nextCursor: FeedCursor | null;
}

export const FEED_PAGE_SIZE = 20;

/**
 * Keyset-paginated feed fetch.
 *
 * Why keyset (cursor) instead of offset/page-number: offset gets slower
 * as the page grows because Postgres scans the skipped rows. Keyset on
 * (published_at DESC, id DESC) stays O(log n) regardless of depth.
 *
 * The (published_at, id) tuple cursor handles ties: two posts published
 * at the exact same instant disambiguate by id.
 *
 * Server returns `limit + 1` rows so we know whether a next page exists
 * without an extra count query.
 */
export async function fetchFeed(args?: {
  limit?: number;
  cursor?: FeedCursor | null;
  signal?: AbortSignal;
  /** Archive reads pass true so Sales Room lots are included (the main
   *  feed hides them â€” see the filter note below). */
  includeSalesRoom?: boolean;
  /** Optional case-insensitive search over title + dek (Archive). */
  search?: string;
}): Promise<FeedPage> {
  const limit = args?.limit ?? FEED_PAGE_SIZE;
  const cursor = args?.cursor ?? null;
  const signal = args?.signal;
  const includeSalesRoom = args?.includeSalesRoom ?? false;
  // PostgREST's or() filter is comma/paren-delimited, so strip those from
  // the user's term to avoid breaking the filter grammar. Plain substring
  // search is enough for the Archive.
  const search = (args?.search ?? '').replace(/[(),*]/g, ' ').trim();

  let q = supabase
    .from('posts_with_counts')
    .select(POST_SELECT)
    .not('published_at', 'is', null)
    .lte('published_at', new Date().toISOString())
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: false })
    .limit(limit + 1);

  if (search) {
    q = q.or(`title.ilike.%${search}%,dek.ilike.%${search}%`);
  }

  if (cursor) {
    // Keyset: (published_at, id) < (cursor.published_at, cursor.id)
    // Postgrest doesn't support row-tuple comparisons directly, so we
    // express it as: published_at < cursor OR (published_at = cursor AND id < cursor.id)
    q = q.or(
      `published_at.lt.${cursor.published_at},and(published_at.eq.${cursor.published_at},id.lt.${cursor.id})`,
    );
  }

  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;

  const rows = (data ?? []).map(shapePost);
  const hasMore = rows.length > limit;
  const items = hasMore ? rows.slice(0, limit) : rows;
  const last = items[items.length - 1];
  const nextCursor: FeedCursor | null = hasMore && last
    ? { published_at: last.published_at!, id: last.id }
    : null;

  // Attach poll metadata so PostCard can render its "Â· Poll" indicator
  // without each caller stitching the data themselves. We don't need
  // viewer-vote state at the feed level (only the post detail page does),
  // so we pass null for viewerId â€” viewer_voted comes back false.
  if (items.length > 0) {
    const viewerId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const postIds = items.map((p) => p.id);

    // Cross-feature attach: polls, events, auctions, viewer-watch state
    // all fetched in parallel against the same id list. Mirrors the
    // polls-only pre-existing pattern.
    const [pollsByPost, eventsByPost, auctionsByPost, watchedIds] = await Promise.all([
      fetchPollOptionsForPosts(postIds, null, signal),
      fetchEventsForPosts(postIds, viewerId, signal),
      fetchAuctionsForPosts(postIds, viewerId, signal),
      viewerId ? fetchWatchedPostIds(postIds, viewerId, signal) : Promise.resolve(new Set<string>()),
    ]);

    for (const item of items) {
      const opts = pollsByPost.get(item.id);
      if (opts && opts.length > 0) item.poll_options = opts;
      const ev = eventsByPost.get(item.id);
      if (ev) item.event = ev;
      const auc = auctionsByPost.get(item.id);
      if (auc) {
        auc.viewer_watching = watchedIds.has(item.id);
        item.auction = auc;
      }
    }
  }

  // Hardcoded V1 exclusion: posts in the Sales Room drawer don't appear
  // in the main feed. Members reach them via /sales-room, /p/{slug},
  // search, etc. The proper drawer-level "include in main feed" toggle
  // ships with the drawer-admin build â€” see plan
  // plans/2026-05-29-events-auctions-calendar.md "Hiding Sales Room
  // posts from the main feed". The Archive opts in via includeSalesRoom.
  const filtered = includeSalesRoom
    ? items
    : items.filter((p) => !(p.drawers ?? []).some((d) => d.slug === 'sales-room'));

  return { items: filtered, nextCursor };
}

export async function fetchPostBySlug(slug: string, signal?: AbortSignal): Promise<Post | null> {
  let q = supabase
    .from('posts_with_counts')
    .select(POST_SELECT)
    .eq('slug', slug);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const post = shapePost(data);

  // Attach feature blocks + viewer state in parallel, same shape as
  // fetchFeed's per-post attachment. viewer_voted on polls is included
  // here (unlike the feed) because the detail page exposes the poll UI.
  const viewerId = (await supabase.auth.getUser()).data.user?.id ?? null;
  const [pollOpts, event, auction, watchedIds, mediaItems] = await Promise.all([
    fetchPollOptionsForPost(post.id, viewerId, signal),
    fetchEventForPost(post.id, viewerId, signal),
    fetchAuctionForPost(post.id, viewerId, signal),
    viewerId ? fetchWatchedPostIds([post.id], viewerId, signal) : Promise.resolve(new Set<string>()),
    fetchPostMediaItems(post, signal),
  ]);
  post.media_items = mediaItems;
  if (pollOpts.length > 0) post.poll_options = pollOpts;
  if (event) post.event = event;
  if (auction) {
    auction.viewer_watching = watchedIds.has(post.id);
    post.auction = auction;
  }
  return post;
}

export async function fetchViewerLikedPostIds(postIds: string[], viewerId: string, signal?: AbortSignal): Promise<Set<string>> {
  if (postIds.length === 0) return new Set();
  let q = supabase
    .from('post_likes')
    .select('post_id')
    .eq('user_id', viewerId)
    .in('post_id', postIds);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return new Set((data ?? []).map((r: any) => r.post_id));
}

export async function togglePostLike(postId: string, viewerId: string, currentlyLiked: boolean): Promise<boolean> {
  if (currentlyLiked) {
    const { error } = await supabase
      .from('post_likes')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', viewerId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from('post_likes')
    .insert({ post_id: postId, user_id: viewerId });
  if (error) throw error;
  return true;
}

// ---------- Polls ---------------------------------------------------------
// Multi-choice: one row in poll_votes per (post, user, option). Toggling an
// option = insert or delete one row. UI reads counts via the
// `poll_options_with_counts` view and overlays the viewer's votes.

/**
 * Fetch poll options + counts for a batch of post ids. Returns a Map keyed
 * by post_id; posts without a poll are simply absent from the map.
 * If `viewerId` is provided, each option's `viewer_voted` reflects the
 * viewer's current state.
 */
export async function fetchPollOptionsForPosts(
  postIds: string[],
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<Map<string, PollOption[]>> {
  const result = new Map<string, PollOption[]>();
  if (postIds.length === 0) return result;

  let optsQ = supabase
    .from('poll_options_with_counts')
    .select('id, post_id, position, label, vote_count')
    .in('post_id', postIds)
    .order('post_id', { ascending: true })
    .order('position', { ascending: true });
  if (signal) optsQ = optsQ.abortSignal(signal);
  const { data: optionRows, error: oErr } = await optsQ;
  if (oErr) throw oErr;

  let votedIds = new Set<string>();
  if (viewerId && (optionRows?.length ?? 0) > 0) {
    let vQ = supabase
      .from('poll_votes')
      .select('option_id')
      .eq('user_id', viewerId)
      .in('post_id', postIds);
    if (signal) vQ = vQ.abortSignal(signal);
    const { data: voteRows, error: vErr } = await vQ;
    if (vErr) throw vErr;
    votedIds = new Set((voteRows ?? []).map((r: any) => r.option_id));
  }

  for (const r of optionRows ?? []) {
    const opt: PollOption = {
      id: r.id,
      post_id: r.post_id,
      position: r.position,
      label: r.label,
      vote_count: r.vote_count ?? 0,
      viewer_voted: votedIds.has(r.id),
    };
    const arr = result.get(r.post_id) ?? [];
    arr.push(opt);
    result.set(r.post_id, arr);
  }
  return result;
}

export async function fetchPollOptionsForPost(
  postId: string,
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<PollOption[]> {
  const map = await fetchPollOptionsForPosts([postId], viewerId, signal);
  return map.get(postId) ?? [];
}

export async function togglePollVote(
  postId: string,
  optionId: string,
  viewerId: string,
  currentlyVoted: boolean,
): Promise<boolean> {
  if (currentlyVoted) {
    const { error } = await supabase
      .from('poll_votes')
      .delete()
      .eq('post_id', postId)
      .eq('option_id', optionId)
      .eq('user_id', viewerId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from('poll_votes')
    .insert({ post_id: postId, option_id: optionId, user_id: viewerId });
  if (error) throw error;
  return true;
}

// ---------- Events --------------------------------------------------------
// One row per event-feature post in post_events; RSVPs are insert/delete
// toggles in event_rsvps (mirrors poll_votes). The going_count comes from
// the post_events_with_counts view in a single round-trip.

const EVENT_SELECT =
  'post_id, starts_at, ends_at, location_kind, location_url, location_address, going_count';

function shapeEvent(row: any, viewerGoing: boolean): PostEvent {
  return {
    post_id: row.post_id,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    location_kind: row.location_kind as EventLocationKind,
    location_url: row.location_url,
    location_address: row.location_address,
    going_count: row.going_count ?? 0,
    viewer_going: viewerGoing,
  };
}

export async function fetchEventsForPosts(
  postIds: string[],
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<Map<string, PostEvent>> {
  const result = new Map<string, PostEvent>();
  if (postIds.length === 0) return result;

  let q = supabase
    .from('post_events_with_counts')
    .select(EVENT_SELECT)
    .in('post_id', postIds);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;

  let goingIds = new Set<string>();
  if (viewerId && (data?.length ?? 0) > 0) {
    let rQ = supabase
      .from('event_rsvps')
      .select('post_id')
      .eq('user_id', viewerId)
      .in('post_id', postIds);
    if (signal) rQ = rQ.abortSignal(signal);
    const { data: rRows, error: rErr } = await rQ;
    if (rErr) throw rErr;
    goingIds = new Set((rRows ?? []).map((r: any) => r.post_id));
  }

  for (const r of data ?? []) {
    result.set(r.post_id, shapeEvent(r, goingIds.has(r.post_id)));
  }
  return result;
}

export async function fetchEventForPost(
  postId: string,
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<PostEvent | null> {
  const map = await fetchEventsForPosts([postId], viewerId, signal);
  return map.get(postId) ?? null;
}

export async function toggleEventRsvp(
  postId: string,
  viewerId: string,
  currentlyGoing: boolean,
): Promise<boolean> {
  if (currentlyGoing) {
    const { error } = await supabase
      .from('event_rsvps')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', viewerId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from('event_rsvps')
    .insert({ post_id: postId, user_id: viewerId });
  if (error) throw error;
  return true;
}

// ---------- Auctions ------------------------------------------------------
// The post_auctions_with_state view bundles config + derived state +
// current top bid + counts in one row, so feed cards and detail pages
// share the same shape. Bid history reads go through auction_bids_public
// (no max_amount). place_bid is an RPC â€” see migration 031.

const AUCTION_SELECT =
  'post_id, lot_number, lot_name, starts_at, ends_at, shipping_info, size_info, material_info, min_bid, ' +
  'shipping_cost, buy_now_price, bought_now_at, ' +
  'state, current_high_amount, current_high_bidder_id, bid_count, bidder_count, watch_count';

function shapeAuction(row: any, viewerId: string | null): PostAuction {
  return {
    post_id: row.post_id,
    lot_number: row.lot_number,
    lot_name: row.lot_name,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    shipping_info: row.shipping_info,
    size_info: row.size_info,
    material_info: row.material_info ?? null,
    min_bid: row.min_bid ?? 100,
    shipping_cost: row.shipping_cost ?? null,
    buy_now_price: row.buy_now_price ?? null,
    bought_now_at: row.bought_now_at ?? null,
    state: row.state as AuctionState,
    current_high_amount: row.current_high_amount ?? 0,
    current_high_bidder_id: row.current_high_bidder_id,
    bid_count: row.bid_count ?? 0,
    bidder_count: row.bidder_count ?? 0,
    watch_count: row.watch_count ?? 0,
    viewer_is_high_bidder: viewerId != null && row.current_high_bidder_id === viewerId,
  };
}

export async function fetchAuctionsForPosts(
  postIds: string[],
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<Map<string, PostAuction>> {
  const result = new Map<string, PostAuction>();
  if (postIds.length === 0) return result;

  let q = supabase
    .from('post_auctions_with_state')
    .select(AUCTION_SELECT)
    .in('post_id', postIds);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;

  for (const r of (data ?? []) as any[]) {
    result.set(r.post_id, shapeAuction(r, viewerId));
  }
  return result;
}

export async function fetchAuctionForPost(
  postId: string,
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<PostAuction | null> {
  const map = await fetchAuctionsForPosts([postId], viewerId, signal);
  return map.get(postId) ?? null;
}

/**
 * Recent bids list for the public bid history on the post detail page.
 * Reads through auction_bids_public so max_amount is never returned to
 * anyone other than the bidder themselves (see migration 031 RLS notes).
 */
export async function fetchRecentBids(
  postId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<AuctionBid[]> {
  let q = supabase
    .from('auction_bids_public')
    .select(
      'id, post_id, bidder_id, amount, created_at, ' +
        'bidder:profiles!auction_bids_bidder_id_fkey ( id, display_name, avatar_url, handle )',
    )
    .eq('post_id', postId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;

  return (data ?? []).map((r: any) => ({
    id: r.id,
    post_id: r.post_id,
    bidder_id: r.bidder_id,
    amount: r.amount,
    created_at: r.created_at,
    bidder: r.bidder ?? undefined,
  }));
}

/**
 * Place a direct bid via the place_bid RPC. The RPC owns all validation
 * (live window, access, self-bid, min_bid floor, $1 increment) and the
 * anti-snipe extension â€” see migration 051. `amountCents` is the literal
 * bid the member is placing; the RPC parameter is still named `new_max`
 * for backwards compatibility (it no longer means a proxy ceiling).
 * Errors thrown by the RPC (auction_ended, bid_too_low, below_min_bidâ€¦)
 * bubble up with the raw message; the UI maps them to user-facing copy.
 */
export async function placeBid(postId: string, amountCents: number): Promise<AuctionBid> {
  const { data, error } = await supabase.rpc('place_bid', {
    target_post_id: postId,
    new_max: amountCents,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    id: row.id,
    post_id: row.post_id,
    bidder_id: row.bidder_id,
    amount: row.amount,
    max_amount: row.max_amount,
    created_at: row.created_at,
  };
}

/**
 * Buy a lot outright via the buy_now RPC. The RPC owns all validation
 * (live window, access, card gate, self-bid, and the first-bid rule â€”
 * Buy Now is only available while the lot has zero bids). It inserts a
 * bid at buy_now_price and ends the lot immediately. Errors thrown by
 * the RPC (no_buy_now, buy_now_unavailable, no_card_on_file, â€¦) bubble
 * up with the raw message; the UI maps them to user-facing copy.
 */
export async function buyNow(postId: string): Promise<void> {
  const { error } = await supabase.rpc('buy_now', { target_post_id: postId });
  if (error) throw error;
}

// ---------- Sales Room payments (card on file + winner charge) -----------
// The bid gate: a member can't bid until they've linked an off-session card.
// has_card_on_file is the server-trusted flag (webhook-written); place_bid
// enforces it. The client reads its own row (RLS: own) only for UX.

/** The viewer's own bidding-card state, or null if they've never linked one. */
export async function fetchMyPaymentMethod(): Promise<MemberPaymentMethod | null> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from('member_payment_methods')
    .select('has_card_on_file, card_brand, card_last4, card_exp_month, card_exp_year')
    .eq('user_id', uid)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    has_card_on_file: !!data.has_card_on_file,
    card_brand: data.card_brand ?? null,
    card_last4: data.card_last4 ?? null,
    card_exp_month: data.card_exp_month ?? null,
    card_exp_year: data.card_exp_year ?? null,
  };
}

// ---------- Bid contact + consent (Register to bid) ----------------------
// Captured before the card step. Backs future bid-activity notifications
// (outbid / result / payment) by email + SMS â€” transactional, not marketing.

export interface BidContact {
  full_name: string;
  phone: string;
  email: string;
  updates_consent: boolean;
}

/** The viewer's saved bidding contact, or null if they haven't registered. */
export async function fetchMyBidContact(): Promise<BidContact | null> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from('bid_contacts')
    .select('full_name, phone, email, updates_consent')
    .eq('user_id', uid)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    full_name: data.full_name,
    phone: data.phone,
    email: data.email,
    updates_consent: !!data.updates_consent,
  };
}

/** Save (insert or update) the viewer's bidding contact + consent. */
export async function saveBidContact(input: {
  full_name: string;
  phone: string;
  email: string;
  consent: boolean;
}): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) throw new Error('Sign in first.');
  const nowIso = new Date().toISOString();
  const { error } = await supabase.from('bid_contacts').upsert(
    {
      user_id: uid,
      full_name: input.full_name,
      phone: input.phone,
      email: input.email,
      updates_consent: input.consent,
      consent_at: input.consent ? nowIso : null,
      updated_at: nowIso,
    },
    { onConflict: 'user_id' },
  );
  if (error) throw error;
}

/** Start the add-card flow: returns a SetupIntent client secret to mount in
 *  Stripe Elements. Throws with a friendly message on failure. */
export async function createSetupIntent(): Promise<string> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch('/api/stripe/setup-intent', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.clientSecret) {
    throw new Error(data?.error ?? 'Could not start card setup.');
  }
  return data.clientSecret as string;
}

/** The viewer's auction-win charge that still needs them to complete payment
 *  on-session (SCA). Null when there's nothing to action. */
export async function fetchMyRequiresActionCharge(): Promise<AuctionCharge | null> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from('auction_charges')
    .select('id, post_id, amount_cents, status, payment_intent_id, created_at')
    .eq('winner_id', uid)
    .eq('status', 'requires_action')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as AuctionCharge) ?? null;
}

/** Fetch the client secret for a requires_action winner charge so the member
 *  can complete SCA in Elements. Server verifies they own the charge. */
export async function fetchAuctionChargeSecret(chargeId: string): Promise<string> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch('/api/stripe/auction-charge-secret', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ charge_id: chargeId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.clientSecret) {
    throw new Error(data?.error ?? 'Could not load the payment.');
  }
  return data.clientSecret as string;
}

/** Remove the saved bidding card (detach in Stripe + clear the gate flag). */
export async function removeMyPaymentMethod(): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch('/api/stripe/payment-method', {
    method: 'DELETE',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error ?? 'Could not remove the card.');
}

/** A reusable card already on the member's Stripe Customer (e.g. from their
 *  subscription), so the modal can offer one-click reuse. Null if none. */
export async function fetchExistingCard(): Promise<{ brand: string | null; last4: string | null } | null> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) return null;
  const res = await fetch('/api/stripe/existing-card', {
    headers: { authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return null;
  return data?.card ?? null;
}

/** Enable bidding using the card already on file (no re-entry). Caller must
 *  have captured the off-session acknowledgement first. */
export async function useExistingCardForBidding(): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch('/api/stripe/use-existing-card', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) throw new Error(data?.error ?? 'Could not enable bidding with your card.');
}

// ---------- Watchlist -----------------------------------------------------
// Generic "track this post" primitive in post_watchers. V1 surfaces it on
// auction lots only, but the table is feature-agnostic so future features
// can reuse it. Mirrors post_likes from migration 008.

export async function fetchWatchedPostIds(
  postIds: string[],
  viewerId: string,
  signal?: AbortSignal,
): Promise<Set<string>> {
  if (postIds.length === 0) return new Set();
  let q = supabase
    .from('post_watchers')
    .select('post_id')
    .eq('user_id', viewerId)
    .in('post_id', postIds);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return new Set((data ?? []).map((r: any) => r.post_id));
}

export async function toggleWatch(
  postId: string,
  viewerId: string,
  currentlyWatching: boolean,
): Promise<boolean> {
  if (currentlyWatching) {
    const { error } = await supabase
      .from('post_watchers')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', viewerId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from('post_watchers')
    .insert({ post_id: postId, user_id: viewerId });
  if (error) throw error;
  return true;
}

/**
 * Fetch the viewer's watched posts as shaped Post rows (for /watchlist).
 * Currently V1 = auction-only, so attach the auction block to each.
 */
export async function fetchWatchlist(viewerId: string, signal?: AbortSignal): Promise<Post[]> {
  let wQ = supabase
    .from('post_watchers')
    .select('post_id, created_at')
    .eq('user_id', viewerId)
    .order('created_at', { ascending: false });
  if (signal) wQ = wQ.abortSignal(signal);
  const { data: watchRows, error: wErr } = await wQ;
  if (wErr) throw wErr;
  const ids = (watchRows ?? []).map((r: any) => r.post_id);
  if (ids.length === 0) return [];

  let pQ = supabase
    .from('posts_with_counts')
    .select(POST_SELECT)
    .in('id', ids);
  if (signal) pQ = pQ.abortSignal(signal);
  const { data: postRows, error: pErr } = await pQ;
  if (pErr) throw pErr;

  const posts = (postRows ?? []).map(shapePost);
  const auctionsByPost = await fetchAuctionsForPosts(ids, viewerId, signal);
  for (const p of posts) {
    const auc = auctionsByPost.get(p.id);
    if (auc) {
      auc.viewer_watching = true; // by definition â€” these are watched posts
      p.auction = auc;
    }
  }
  // Sort: live first (by ends_at asc), then scheduled (starts_at asc), then ended (ends_at desc).
  posts.sort((a, b) => {
    const stateRank = (s?: AuctionState | null) =>
      s === 'live' ? 0 : s === 'scheduled' ? 1 : s === 'ended' ? 2 : 3;
    const sa = stateRank(a.auction?.state);
    const sb = stateRank(b.auction?.state);
    if (sa !== sb) return sa - sb;
    if (sa === 0) return (a.auction?.ends_at ?? '').localeCompare(b.auction?.ends_at ?? '');
    if (sa === 1) return (a.auction?.starts_at ?? '').localeCompare(b.auction?.starts_at ?? '');
    if (sa === 2) return (b.auction?.ends_at ?? '').localeCompare(a.auction?.ends_at ?? '');
    return 0;
  });
  return posts;
}

// ---------- Sales Room ----------------------------------------------------
// Lens over posts with an auction feature attached. Three buckets:
//   live now           â€” sorted by ends_at ascending
//   coming soon        â€” sorted by starts_at ascending
//   recently ended     â€” last 30 days, ends_at descending

export interface SalesRoomBuckets {
  live: Post[];
  scheduled: Post[];
  ended: Post[];
}

export async function fetchSalesRoomPosts(signal?: AbortSignal): Promise<SalesRoomBuckets> {
  // Pull every auction row, then hydrate the posts in one round-trip.
  // The view is small (one row per auctioned post) so we don't paginate
  // here; if the catalog ever grows to hundreds of live lots we can add
  // bucket-specific limits.
  let aQ = supabase
    .from('post_auctions_with_state')
    .select(AUCTION_SELECT);
  if (signal) aQ = aQ.abortSignal(signal);
  const { data: aucRows, error: aErr } = await aQ;
  if (aErr) throw aErr;

  const viewerId = (await supabase.auth.getUser()).data.user?.id ?? null;
  const auctions = (aucRows ?? []).map((r: any) => shapeAuction(r, viewerId));
  if (auctions.length === 0) return { live: [], scheduled: [], ended: [] };

  const postIds = auctions.map((a) => a.post_id);
  let pQ = supabase
    .from('posts_with_counts')
    .select(POST_SELECT)
    .in('id', postIds);
  if (signal) pQ = pQ.abortSignal(signal);
  const { data: postRows, error: pErr } = await pQ;
  if (pErr) throw pErr;
  const posts = (postRows ?? []).map(shapePost);
  const byId = new Map(posts.map((p) => [p.id, p]));

  const watchedIds = viewerId ? await fetchWatchedPostIds(postIds, viewerId, signal) : new Set<string>();

  const live: Post[] = [];
  const scheduled: Post[] = [];
  const ended: Post[] = [];
  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days ago

  for (const auc of auctions) {
    const post = byId.get(auc.post_id);
    if (!post) continue;
    auc.viewer_watching = watchedIds.has(auc.post_id);
    post.auction = auc;
    if (auc.state === 'live') live.push(post);
    else if (auc.state === 'scheduled') scheduled.push(post);
    else if (auc.state === 'ended' && Date.parse(auc.ends_at) >= cutoff) ended.push(post);
  }

  live.sort((a, b) => (a.auction!.ends_at).localeCompare(b.auction!.ends_at));
  scheduled.sort((a, b) => (a.auction!.starts_at).localeCompare(b.auction!.starts_at));
  ended.sort((a, b) => (b.auction!.ends_at).localeCompare(a.auction!.ends_at));

  return { live, scheduled, ended };
}

// ---------- Calendar (external cultural events) ---------------------------
// The /calendar route is an editorial almanac of EXTERNAL cultural moments
// (Met Gala, Fashion Week, Earth Day, etc.) â€” NOT club events or auctions.
// It reads exclusively from public.cultural_events. Admins curate the set
// via the create/update/delete mutations below.

const CULTURAL_EVENT_SELECT =
  'id, title, description, event_date, end_date, category, external_url, is_published, country, city, created_by, created_at';

/**
 * Fetch cultural events that overlap [rangeStart, rangeEnd] (both
 * 'YYYY-MM-DD'). An event overlaps the window when it starts on/before the
 * range end AND its last day (end_date, or event_date when single-day)
 * falls on/after the range start â€” so multi-day moments spanning into the
 * month are included. Ordered by event_date.
 */
export async function fetchCulturalEvents(
  rangeStart: string,
  rangeEnd: string,
  signal?: AbortSignal,
): Promise<CulturalEvent[]> {
  let q = supabase
    .from('cultural_events')
    .select(CULTURAL_EVENT_SELECT)
    .lte('event_date', rangeEnd)
    .or(`end_date.gte.${rangeStart},and(end_date.is.null,event_date.gte.${rangeStart})`)
    .order('event_date', { ascending: true });
  if (signal) q = q.abortSignal(signal);

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as CulturalEvent[];
}

export interface CulturalEventInput {
  title: string;
  description?: string | null;
  event_date: string;                  // 'YYYY-MM-DD'
  end_date?: string | null;            // 'YYYY-MM-DD'
  category: CulturalEventCategory;
  external_url?: string | null;
  is_published?: boolean;
  country?: string | null;             // ISO alpha-2 location code
  city?: string | null;                // free-text city/area
}

/** Admin-only: create a cultural event. created_by is set from the caller. */
export async function createCulturalEvent(
  input: CulturalEventInput,
): Promise<CulturalEvent> {
  const createdBy = (await supabase.auth.getUser()).data.user?.id ?? null;
  const { data, error } = await supabase
    .from('cultural_events')
    .insert({
      title: input.title,
      description: input.description ?? null,
      event_date: input.event_date,
      end_date: input.end_date ?? null,
      category: input.category,
      external_url: input.external_url ?? null,
      is_published: input.is_published ?? true,
      country: input.country ?? null,
      city: input.city ?? null,
      created_by: createdBy,
    })
    .select(CULTURAL_EVENT_SELECT)
    .single();
  if (error) throw error;
  return data as CulturalEvent;
}

/** Admin-only: patch an existing cultural event. */
export async function updateCulturalEvent(
  id: string,
  patch: Partial<CulturalEventInput>,
): Promise<CulturalEvent> {
  const { data, error } = await supabase
    .from('cultural_events')
    .update(patch)
    .eq('id', id)
    .select(CULTURAL_EVENT_SELECT)
    .single();
  if (error) throw error;
  return data as CulturalEvent;
}

/** Admin-only: delete a cultural event. */
export async function deleteCulturalEvent(id: string): Promise<void> {
  const { error } = await supabase.from('cultural_events').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Comments ------------------------------------------------------

const COMMENT_SELECT = `
  id, post_id, parent_id, author_id, body, like_count, is_hidden, created_at,
  author:profiles!comments_author_id_fkey ( id, display_name, avatar_url, handle, is_founding_member )
`;

/**
 * Nest a flat list of comments into top-level + replies tree.
 *
 * Exported because pagination merges pages of flat comments client-side
 * before nesting â€” nesting per-page would orphan replies whose parent
 * landed on a different page.
 */
export function nestComments(rows: any[]): Comment[] {
  const byId: Record<string, Comment> = {};
  const top: Comment[] = [];
  for (const r of rows) {
    byId[r.id] = { ...r, author: r.author ?? undefined, replies: [] };
  }
  for (const r of rows) {
    const c = byId[r.id];
    if (r.parent_id && byId[r.parent_id]) {
      byId[r.parent_id].replies!.push(c);
    } else {
      top.push(c);
    }
  }
  // Top-level: most-liked first, then oldest first
  top.sort((a, b) => (b.like_count - a.like_count) || (a.created_at.localeCompare(b.created_at)));
  // Replies: oldest first
  for (const c of top) c.replies!.sort((a, b) => a.created_at.localeCompare(b.created_at));
  return top;
}

export interface CommentsCursor {
  created_at: string;
  id: string;
}

export interface CommentsPage {
  /** Flat comments â€” caller merges across pages and nests once. */
  flat: Comment[];
  nextCursor: CommentsCursor | null;
}

export const COMMENTS_PAGE_SIZE = 15;

/**
 * Paginated comment fetch â€” returns FLAT comments.
 *
 * Chronological order (oldest first). Caller merges all loaded pages
 * into one flat array, then calls nestComments() to build the tree.
 * Returning nested per-page would orphan replies whose parent lives
 * on a different page.
 *
 * Per-thread reply pagination (e.g. "load 50 more replies under this
 * comment") is out of scope for this pass â€” flagged in the build log
 * as a follow-up if a single thread ever blows past 1000 replies.
 */
export async function fetchComments(
  postId: string,
  args?: { limit?: number; cursor?: CommentsCursor | null; signal?: AbortSignal },
): Promise<CommentsPage> {
  const limit = args?.limit ?? COMMENTS_PAGE_SIZE;
  const cursor = args?.cursor ?? null;
  const signal = args?.signal;

  let q = supabase
    .from('comments')
    .select(COMMENT_SELECT)
    .eq('post_id', postId)
    .eq('is_hidden', false)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true })
    .limit(limit + 1);

  if (cursor) {
    // (created_at, id) > (cursor.created_at, cursor.id)
    q = q.or(
      `created_at.gt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.gt.${cursor.id})`,
    );
  }

  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;

  // Supabase types the joined `author` relation as an array because it
  // can't statically know the FK is one-to-one. Cast through unknown to
  // hand the caller properly-shaped Comments. Same pattern as the
  // shapePost / nestComments paths above.
  const rows = (data ?? []) as unknown as Comment[];
  const hasMore = rows.length > limit;
  const flat = hasMore ? rows.slice(0, limit) : rows;
  const last = flat[flat.length - 1];
  const nextCursor: CommentsCursor | null = hasMore && last
    ? { created_at: last.created_at, id: last.id }
    : null;

  return { flat, nextCursor };
}

export async function fetchViewerLikedCommentIds(commentIds: string[], viewerId: string, signal?: AbortSignal): Promise<Set<string>> {
  if (commentIds.length === 0) return new Set();
  let q = supabase
    .from('comment_likes')
    .select('comment_id')
    .eq('user_id', viewerId)
    .in('comment_id', commentIds);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return new Set((data ?? []).map((r: any) => r.comment_id));
}

export async function toggleCommentLike(commentId: string, viewerId: string, currentlyLiked: boolean): Promise<boolean> {
  if (currentlyLiked) {
    const { error } = await supabase
      .from('comment_likes')
      .delete()
      .eq('comment_id', commentId)
      .eq('user_id', viewerId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from('comment_likes')
    .insert({ comment_id: commentId, user_id: viewerId });
  if (error) throw error;
  return true;
}

export async function insertComment(args: {
  postId: string;
  authorId: string;
  body: string;
  parentId?: string | null;
}): Promise<Comment> {
  // maybeSingle() instead of single() â€” don't throw if the post-insert
  // SELECT returns 0 rows due to a RLS edge case (was causing replies to
  // hang on "Postingâ€¦"). We build a client-side fallback from what we
  // know + the viewer's profile when the row doesn't come back.
  const { data, error } = await supabase
    .from('comments')
    .insert({
      post_id: args.postId,
      author_id: args.authorId,
      body: args.body,
      parent_id: args.parentId ?? null,
    })
    .select(COMMENT_SELECT)
    .maybeSingle();
  if (error) throw error;
  if (data) return { ...(data as any), replies: [] };

  // Fallback: insert succeeded but SELECT didn't return. Fetch the viewer's
  // profile so we can stamp the new comment's author fields client-side.
  const { data: author } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url, handle, is_founding_member')
    .eq('id', args.authorId)
    .maybeSingle();
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    post_id: args.postId,
    parent_id: args.parentId ?? null,
    author_id: args.authorId,
    body: args.body,
    like_count: 0,
    is_hidden: false,
    created_at: now,
    author: (author as any) ?? undefined,
    replies: [],
  };
}

// ---------- Profiles ------------------------------------------------------

export async function fetchProfileByHandle(handle: string, signal?: AbortSignal): Promise<Profile | null> {
  let q = supabase
    .from('profiles')
    .select('id, display_name, handle, avatar_url, banner_url, banner_position, bio, fabric_selection, role, is_founding_member, instagram_handle, social_links, location, country, referral_code, created_at')
    .eq('handle', handle);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return (data as Profile | null) ?? null;
}

export async function fetchProfileById(id: string, signal?: AbortSignal): Promise<Profile | null> {
  let q = supabase
    .from('profiles')
    .select('id, display_name, handle, avatar_url, banner_url, banner_position, bio, fabric_selection, role, is_founding_member, instagram_handle, social_links, location, country, referral_code, created_at')
    .eq('id', id);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return (data as Profile | null) ?? null;
}

export async function countPostsByAuthor(authorId: string, signal?: AbortSignal): Promise<number> {
  let q = supabase
    .from('posts')
    .select('id', { count: 'exact', head: true })
    .eq('author_id', authorId)
    .not('published_at', 'is', null);
  if (signal) q = q.abortSignal(signal);
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

export interface ProfileEditPatch {
  display_name?: string;
  handle?: string | null;
  bio?: string | null;
  fabric_selection?: string | null;
  instagram_handle?: string | null;
  social_links?: Record<string, string> | null;
  location?: string | null;
  country?: string | null;
  avatar_url?: string | null;
  banner_url?: string | null;
  /** CSS background-position for the banner focal point, e.g. '50% 35%'. */
  banner_position?: string | null;
  /** Per-user accent; null clears the personal pick (back to global default). */
  accent?: string | null;
  accent_strong?: string | null;
}

export async function updateOwnProfile(userId: string, patch: ProfileEditPatch): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', userId);
  if (error) throw error;
}

export async function uploadProfileImage(args: {
  userId: string;
  kind: 'avatar' | 'banner';
  file: File;
}): Promise<string> {
  const bucket = args.kind === 'avatar' ? 'avatars' : 'banners';
  // Avatars are displayed at most ~256px, banners at ~1600px wide. Compress
  // before upload â€” see src/lib/image-compress.ts for the deferred-CF-Images
  // context.
  const compressed = await compressImage(args.file, {
    maxDim: args.kind === 'avatar' ? 1024 : 2048,
    quality: 0.85,
  });
  const ext = compressed.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${args.userId}/${args.kind}-${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from(bucket)
    .upload(path, compressed, { upsert: true, contentType: compressed.type });
  if (upErr) throw upErr;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

// ---------- Admin: testers + content --------------------------------------
// (Access gate removed in Phase 4 â€” RequireAccess now reads the JWT
// `has_active_access` claim directly via src/lib/jwt-claims.ts. The
// underlying public.has_active_access(uuid) Postgres function still
// exists in the DB so any external callers keep working.)

export async function fetchAllowedEmails(signal?: AbortSignal): Promise<Array<{
  email: string;
  display_name: string | null;
  status: 'invited' | 'logged-in' | 'active';
  tags: string[];
  first_login_at: string | null;
}>> {
  let q = supabase
    .from('allowed_emails')
    .select('email, display_name, status, tags, first_login_at')
    .order('invited_at', { ascending: true });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as any[];
}

export async function fetchAllPostsAdmin(signal?: AbortSignal): Promise<Post[]> {
  // Admin sees drafts too.
  let q = supabase
    .from('posts_with_counts')
    .select(POST_SELECT)
    .order('created_at', { ascending: false });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map(shapePost);
}

/** A cf_stream video whose encode failed, with the post(s) that use it. */
export interface FailedEncode {
  id: string;
  provider_id: string;
  error_detail: string | null;
  created_at: string;
  posts: { id: string; slug: string; title: string }[];
}

/**
 * Admin: cf_stream videos whose encode failed (status='failed'), newest first,
 * with the post(s) that reference them so the team can jump straight to a
 * re-upload. status + error_detail are set by the cf-stream webhook
 * (functions/api/webhooks/cf-stream.ts). Admin-only in practice â€” RLS only
 * exposes non-'ready' media to admins/developers.
 */
export async function fetchFailedEncodes(signal?: AbortSignal): Promise<FailedEncode[]> {
  let q = supabase
    .from('media')
    .select('id, provider_id, error_detail, created_at, post_media ( posts ( id, slug, title ) )')
    .eq('kind', 'video')
    .eq('status', 'failed')
    .order('created_at', { ascending: false });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: row.id,
    provider_id: row.provider_id,
    error_detail: row.error_detail ?? null,
    created_at: row.created_at,
    posts: (row.post_media ?? []).map((pm: any) => pm.posts).filter(Boolean),
  }));
}

/**
 * Admin: take a draft live (publish = true sets published_at = now) or pull a
 * live post back to draft (publish = false clears published_at). Returns the
 * new published_at value (ISO string, or null when reverted to draft).
 */
export async function setPostPublished(postId: string, publish: boolean): Promise<string | null> {
  const published_at = publish ? new Date().toISOString() : null;
  const { error } = await supabase
    .from('posts')
    .update({ published_at })
    .eq('id', postId);
  if (error) throw error;
  return published_at;
}

/**
 * Admin: schedule a post by setting published_at to a specific time. A future
 * time leaves the post hidden until then (RLS gates on published_at <= now()),
 * so it surfaces automatically with no cron. Pass null to revert to draft, or a
 * past/now ISO to publish immediately. Returns the stored value.
 */
export async function setPostSchedule(postId: string, publishAt: string | null): Promise<string | null> {
  const { error } = await supabase
    .from('posts')
    .update({ published_at: publishAt })
    .eq('id', postId);
  if (error) throw error;
  return publishAt;
}

/**
 * Admin: permanently delete a post. Used by the wizard's "Discard draft" and
 * the content manager's delete action. The posts_delete RLS policy (migration
 * 015) restricts this to staff. Child rows in post_media / post_drawers /
 * poll_options / post_events / post_auctions are removed by ON DELETE CASCADE.
 */
export async function adminDeletePost(postId: string): Promise<void> {
  const { error } = await supabase.from('posts').delete().eq('id', postId);
  if (error) throw error;
}

export interface NewPostInput {
  title: string;
  slug: string;
  dek?: string | null;
  body_md?: string | null;
  drawer_ids: string[];
  cover_url?: string | null;
  video_hls_url?: string | null;
  aspect_ratio?: number | null;
}

export async function adminCreatePost(authorId: string, input: NewPostInput): Promise<string> {
  // Create media row first (if cover/video).
  let mediaId: string | null = null;
  if (input.video_hls_url || input.cover_url) {
    const { data: m, error: mErr } = await supabase
      .from('media')
      .insert({
        kind: input.video_hls_url ? 'video' : 'image',
        provider: 'cf_stream',
        provider_id: input.video_hls_url || input.cover_url || 'manual',
        aspect_ratio: input.aspect_ratio ?? null,
        thumbnail_url: input.cover_url,
        status: 'ready',
        uploaded_by: authorId,
      })
      .select('id')
      .single();
    if (mErr) throw mErr;
    mediaId = m.id;
  }

  const { data: post, error: pErr } = await supabase
    .from('posts')
    .insert({
      slug: input.slug,
      title: input.title,
      dek: input.dek ?? null,
      body_md: input.body_md ?? null,
      post_type: input.video_hls_url ? 'video' : input.cover_url ? 'image' : 'text',
      author_id: authorId,
      primary_media_id: mediaId,
      published_at: new Date().toISOString(),
    })
    .select('id, slug')
    .single();
  if (pErr) throw pErr;

  if (input.drawer_ids.length) {
    const rows = input.drawer_ids.map((d) => ({ post_id: post.id, drawer_id: d }));
    const { error: jErr } = await supabase.from('post_drawers').insert(rows);
    if (jErr) throw jErr;
  }

  return post.slug;
}

export async function uploadPostCover(args: { authorId: string; file: File }): Promise<string> {
  // Post covers display up to full feed-card width (~1200px). Same compression
  // pass as avatars/banners.
  const compressed = await compressImage(args.file, { maxDim: 2048, quality: 0.85 });
  const ext = compressed.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${args.authorId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('post-covers')
    .upload(path, compressed, { upsert: false, contentType: compressed.type });
  if (error) throw error;
  const { data } = supabase.storage.from('post-covers').getPublicUrl(path);
  return data.publicUrl;
}

// ---------- Redesign trace resource upload ----------------------------------
// The downloadable trace for a Redesign Challenge. Unlike covers this is a
// document members print, so PDFs upload byte-for-byte (no canvas pass —
// compressImage rejects non-images); images still get the cover treatment.

const TRACE_PDF_LIMIT_BYTES = 25 * 1024 * 1024;

export async function uploadPostResource(args: { authorId: string; file: File }): Promise<string> {
  const { authorId, file } = args;
  // Drag-and-dropped files can arrive with an empty MIME type on some
  // platforms; fall back to the extension.
  const isPdf = file.type === 'application/pdf' || (!file.type && /\.pdf$/i.test(file.name));
  let blob: File = file;
  let ext = 'pdf';
  if (isPdf) {
    if (file.size > TRACE_PDF_LIMIT_BYTES) throw new Error('PDF is too large (max 25MB).');
  } else if (file.type.startsWith('image/')) {
    blob = await compressImage(file, { maxDim: 2048, quality: 0.85 });
    ext = blob.name.split('.').pop()?.toLowerCase() || 'jpg';
  } else {
    throw new Error('Please upload a PDF or an image.');
  }
  const path = `${authorId}/trace-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('post-covers')
    .upload(path, blob, {
      upsert: false,
      contentType: isPdf ? 'application/pdf' : blob.type,
    });
  if (error) throw error;
  return supabase.storage.from('post-covers').getPublicUrl(path).data.publicUrl;
}

// ---------- Drawer door covers ---------------------------------------------
// A drawer-level cover (drawers.cover_image_url) fronts that closet's door
// cavity on the feed, beating the latest post's media (see Closet.tsx).
// Stored in the public post-covers bucket (admin/dev write) under
// door-covers/<drawer_id>/.

export async function uploadDrawerDoorCover(args: { drawerId: string; file: File }): Promise<string> {
  // Door cavities render at most a few hundred px wide — same compression
  // pass as post covers is plenty.
  const compressed = await compressImage(args.file, { maxDim: 1600, quality: 0.85 });
  const ext = compressed.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `door-covers/${args.drawerId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('post-covers')
    .upload(path, compressed, { upsert: false, contentType: compressed.type });
  if (error) throw error;
  return supabase.storage.from('post-covers').getPublicUrl(path).data.publicUrl;
}

export async function setDrawerCoverImage(drawerId: string, url: string | null): Promise<void> {
  const { error } = await supabase
    .from('drawers')
    .update({ cover_image_url: url })
    .eq('id', drawerId);
  if (error) throw error;
}

// ---------- Redesign Challenge: drawer default resource -------------------
// A Redesign Challenge inherits its trace-resource PDF from the drawer's
// default_resource_url; overriding it on a post updates the drawer default
// going forward ("save as default from then on"). The wizard just calls
// applyResourceDefault after creating the post â€” the read/compare/write
// logic lives here so the UI stays thin.

export async function fetchDrawerDefaultResource(
  drawerId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  let q = supabase
    .from('drawers')
    .select('default_resource_url')
    .eq('id', drawerId);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return (data?.default_resource_url as string | null) ?? null;
}

export async function setDrawerDefaultResource(
  drawerId: string,
  resourceUrl: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('drawers')
    .update({ default_resource_url: resourceUrl })
    .eq('id', drawerId);
  if (error) throw error;
}

/**
 * "Save as default from then on": if a redesign post was created with a
 * resource_url that differs from the drawer's stored default, persist it as
 * the new drawer default. No-op when the url matches (or is empty). Applied
 * across every drawer the post belongs to.
 */
export async function applyResourceDefault(
  drawerIds: string[],
  resourceUrl: string | null | undefined,
): Promise<void> {
  const url = resourceUrl?.trim() || null;
  if (!url || drawerIds.length === 0) return;
  for (const drawerId of drawerIds) {
    const current = await fetchDrawerDefaultResource(drawerId);
    if (current !== url) await setDrawerDefaultResource(drawerId, url);
  }
}

// ---------- Redesign Challenge: submissions -------------------------------
// One editable submission per (post, author) â€” UNIQUE(post_id, author_id),
// see migration 039. like_count is maintained by a DB trigger via
// post_submission_likes; toggleSubmissionLike mirrors toggleCommentLike.

const SUBMISSION_SELECT = `
  id, post_id, author_id, image_url, body, like_count, created_at, updated_at,
  author:profiles!post_submissions_author_id_fkey ( id, display_name, avatar_url, handle )
`;

function shapeSubmission(row: any, viewerLiked: boolean): Submission {
  return {
    id: row.id,
    post_id: row.post_id,
    author_id: row.author_id,
    image_url: row.image_url,
    body: row.body ?? null,
    like_count: row.like_count ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
    author: row.author ?? undefined,
    viewer_liked: viewerLiked,
  };
}

/**
 * Fetch all submissions for a Redesign Challenge post, ranked most-liked
 * first (then newest). viewer_liked reflects the viewer's like state when a
 * viewerId is supplied.
 */
export async function fetchSubmissions(
  postId: string,
  viewerId: string | null,
  signal?: AbortSignal,
): Promise<Submission[]> {
  let q = supabase
    .from('post_submissions')
    .select(SUBMISSION_SELECT)
    .eq('post_id', postId)
    .order('like_count', { ascending: false })
    .order('created_at', { ascending: false });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  const rows = (data ?? []) as any[];

  let likedIds = new Set<string>();
  if (viewerId && rows.length > 0) {
    let lQ = supabase
      .from('post_submission_likes')
      .select('submission_id')
      .eq('user_id', viewerId)
      .in('submission_id', rows.map((r) => r.id));
    if (signal) lQ = lQ.abortSignal(signal);
    const { data: likeRows, error: lErr } = await lQ;
    if (lErr) throw lErr;
    likedIds = new Set((likeRows ?? []).map((r: any) => r.submission_id));
  }

  return rows.map((r) => shapeSubmission(r, likedIds.has(r.id)));
}

/**
 * Insert or update the member's single submission for a post. Uses upsert on
 * the (post_id, author_id) unique constraint so a member editing their entry
 * replaces it in place (and the updated_at trigger touches the row).
 */
export async function upsertSubmission(args: {
  postId: string;
  authorId: string;
  imageUrl: string;
  body?: string | null;
}): Promise<Submission> {
  const { data, error } = await supabase
    .from('post_submissions')
    .upsert(
      {
        post_id: args.postId,
        author_id: args.authorId,
        image_url: args.imageUrl,
        body: args.body ?? null,
      },
      { onConflict: 'post_id,author_id' },
    )
    .select(SUBMISSION_SELECT)
    .single();
  if (error) throw error;
  return shapeSubmission(data, false);
}

export async function deleteSubmission(submissionId: string): Promise<void> {
  const { error } = await supabase
    .from('post_submissions')
    .delete()
    .eq('id', submissionId);
  if (error) throw error;
}

/**
 * Toggle a like on a submission. Mirrors toggleCommentLike exactly: insert or
 * delete one post_submission_likes row; the DB trigger maintains like_count.
 */
export async function toggleSubmissionLike(
  submissionId: string,
  viewerId: string,
  currentlyLiked: boolean,
): Promise<boolean> {
  if (currentlyLiked) {
    const { error } = await supabase
      .from('post_submission_likes')
      .delete()
      .eq('submission_id', submissionId)
      .eq('user_id', viewerId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from('post_submission_likes')
    .insert({ submission_id: submissionId, user_id: viewerId });
  if (error) throw error;
  return true;
}

/**
 * Upload a single member submission image and return its public URL. Mirrors
 * uploadPostCover but writes to the per-member `submissions` bucket (migration
 * 040), keyed under the member's auth.uid()-prefixed folder so bucket RLS
 * allows the write.
 */
export async function uploadSubmissionImage(args: { userId: string; file: File }): Promise<string> {
  const compressed = await compressImage(args.file, { maxDim: 2048, quality: 0.85 });
  const ext = compressed.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${args.userId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('submissions')
    .upload(path, compressed, { upsert: false, contentType: compressed.type });
  if (error) throw error;
  const { data } = supabase.storage.from('submissions').getPublicUrl(path);
  return data.publicUrl;
}
